import React, { lazy, Suspense } from 'react';
import { RETURN_PATH_LOCAL_STORAGE_KEY } from "core/common/constants/localstorage";
import { navigate } from "raviger";


// Home & Delivery
const OrderConfirmationPage = lazy(() => import("pages/Delivery/OrderConfirmation"));
const HomePage = lazy(() => import("pages/Home/HomePage"));
const CouponsPage = lazy(() => import("pages/Delivery/Coupons/Coupons"));
const DeliveryAreas = lazy(() => import("pages/Delivery/DeliveryAreas/DeliveryAreas"));
const Menu = lazy(() => import("pages/Delivery/Menu/Menu"));
const ReportsPage = lazy(() => import("pages/Delivery/Reports/Reports"));
const SettingsPage = lazy(() => import("pages/Delivery/Settings/SettingsPage"));

// Auth
const LoginPage = lazy(() => import("pages/Auth/Login/LoginPage"));
const OtpAdminLogin = lazy(() => import("pages/Auth/OtpAdminLogin/OtpAdminLogin"));
const OtpLoginPage = lazy(() => import("pages/Auth/OtpLogin/OtpLoginPage"));
const ActivatePage = lazy(() => import("pages/Auth/Activate/ActivatePage"));
const BiometricsRegistration = lazy(() => import("pages/Auth/BiometricsRegistration/BiometricsRegistration"));
const ResetPasswordPage = lazy(() => import("pages/Auth/ResetPassword/ResetPasswordPage"));
const SetANewPasswordPage = lazy(() => import("pages/Auth/SetANewPassword/SetANewPasswordPage"));
const AdminAccountSelectionAfterLoginPage = lazy(() => import("pages/Auth/AdminAccountSelectionAfterLogin/AdminAccountSelectionAfterLoginPage"));

// General Pages
const SoundboxPage = lazy(() => import("pages/Soundbox"));
const AdsPage = lazy(() => import("pages/Ads/AdsPage"));
const MainPage = lazy(() => import("pages/Main/MainPage"));
const PricelistsPage = lazy(() => import("pages/Pricelists/PricelistsPage"));
const ReviewsPage = lazy(() => import("pages/Reviews/ReviewsPage"));
const ContactPage = lazy(() => import("pages/Contact/ContactPage"));
const ContactListPage = lazy(() => import("pages/ContactList/ContactListPage"));
const CallsPage = lazy(() => import("pages/Calls"));
const ChangeRequestPage = lazy(() => import("pages/ChangeRequests/ChangeRequests"));
const OpeningHoursPage = lazy(() => import("pages/OpeningHours/OpeningHoursPage"));
const OperationHistoryPage = lazy(() => import("pages/OperationHistory/OperationHistoryPage"));
const DiagnosticPage = lazy(() => import("pages/Diagnostic/DiagnosticPage"));
const PPLPage = lazy(() => import("pages/PPLPage/PPLPage")); // Leads
const PriceListPage = lazy(() => import("pages/PriceList/PriceList"));
const OfirSite = lazy(() => import("pages/OfirSite/OfirSite"));
const E404Page = lazy(() => import("pages/ErrorPages/E404Page"));

const FormikExamplePage = lazy(() => import("pages/Playground/FormikExample/FormikExamplePage"));
const TimeSpanValidaionSndBox = lazy(() => import("pages/Playground/TimeSpanYupValidation/time-span-validaion.page"));

const InvoicesPage = lazy(() => import("pages/Invoices"));

// Business & Golden Pages
const BusinessDetailsPage = lazy(() => import("pages/BusinessDetails/BusinessDetailsPage"));
const BusinessOverviewPage = lazy(() => import("pages/BusinessOverview/BusinessOverviewPage"));
const GalleriesMainPage = lazy(() => import("pages/Galleries/GalleriesMainPage"));
const DescriptionPage = lazy(() => import("pages/GoldenPages/Description/DescriptionPage"));
const PicsPage = lazy(() => import("pages/GoldenPages/Pics/PicsPage"));

// Membership
const MemberPage = lazy(() => import("pages/Membership/Member/member.page"));
const AdminNewPage = lazy(() => import("../../pages/Membership/Admin/admin-create.page"));
const AdminDeletePage = lazy(() => import("../../pages/Membership/Admin/admin-delete.page"));
const RulesPageEdit = lazy(() => import("pages/Membership/Rules/rules-edit-page"));
const RulesPageSelect = lazy(() => import("pages/Membership/Rules/rules-select-page"));
const RulesGroupPageEdit = lazy(() => import("pages/Membership/RulesGroups/rules-group-edit-page"));
const RulesGroupPageSelect = lazy(() => import("pages/Membership/RulesGroups/rules-group-select-page"));

// Zap & Statistics
const ReportByPlatform = lazy(() => import("pages/Statistics/ReportByPlatform/ReportByPlatform"));
const ZapCouponsPage = lazy(() => import("pages/ZapCoupons/ZapCouponsPage"));
const ZapXOverviewPage = lazy(() => import("pages/ZapXOverview/ZapXOverviewPage"));


// --- Components ---

const Loader = () => <div style={{ padding: 20, textAlign: 'center' }}>טוען...</div>;

const load = (Component, props = {}) => (
    <Suspense fallback={<Loader />}>
        <Component {...props} />
    </Suspense>
);


// --- Routes ---

const nonProtectedRoutes = {
    "/login": () => load(LoginPage),
    "/_soundbox": () => load(SoundboxPage),
    "/otplogin": () => load(OtpLoginPage),
    "/otp-admin-login": () => load(OtpAdminLogin),
    "/biometrics-registration": () => load(BiometricsRegistration),

    "/password/forget": () => load(ResetPasswordPage),
    "/password/reset": () => load(SetANewPasswordPage),
    "/password/activate": () => load(ActivatePage),
    "/diagnostic": () => load(DiagnosticPage),
};

const protectedRoutes = {
    "/home": () => load(HomePage),

    "/order-confirmation": () => load(OrderConfirmationPage),

    "/formikexample": () => load(FormikExamplePage),
    "/time-span-validaion-sndbox": () => load(TimeSpanValidaionSndBox),
    // "/otp-admin-login": () => load(OtpAdminLogin), // Duplicate in original, kept if intentional, otherwise remove
    "/contact": () => load(ContactPage),
    "/main": () => load(MainPage),
    "/ads": () => load(AdsPage),

    "/statistics": () => load(HomePage),

    "/report-by-platform": () => load(ReportByPlatform),
    "/ofir-site": () => load(OfirSite),
    "/reviews": () => load(ReviewsPage),
    "/pricelists": () => load(PricelistsPage),

    "/invoices": () => load(InvoicesPage),
    "/invoices/mishlohim": () => load(InvoicesPage),
    "/invoices/easycount": () => load(InvoicesPage),
    "/invoices/zapx": () => load(InvoicesPage),

    "/golden-pages-description": () => load(DescriptionPage),
    "/golden-pages-pics": () => load(PicsPage),
    "/galleries*": () => load(GalleriesMainPage, { basePath: "/galleries" }),
    "/business-details": () => load(BusinessDetailsPage),
    "/business-overview": () => load(BusinessOverviewPage),
    "/zapx-overview": () => load(ZapXOverviewPage),
    "/contactList": () => load(ContactListPage),
    "/calls": () => load(CallsPage),
    "/admin-account-selection": () => load(AdminAccountSelectionAfterLoginPage),
    "/opening-hours": () => load(OpeningHoursPage),
    "/change-requests": () => load(ChangeRequestPage),
    "/leads": () => load(PPLPage),

    "/membership/admin-new": () => load(AdminNewPage),
    "/membership/admin-delete": () => load(AdminDeletePage),
    "/membership/member": () => load(MemberPage),
    "/membership/rules": () => load(RulesPageSelect),
    "/membership/rule": () => load(RulesPageEdit),
    "/membership/rule-groups": () => load(RulesGroupPageSelect),
    "/membership/rule-group": () => load(RulesGroupPageEdit),
    "/operation-history": () => load(OperationHistoryPage),
    "/price-list": () => load(PriceListPage),
    "/zap-coupons": () => load(ZapCouponsPage),

    "/delivery-areas": () => load(DeliveryAreas),
    "/menu": () => load(Menu),
    "/settings": () => load(SettingsPage),
    "/coupons": () => load(CouponsPage),
    "/reports": () => load(ReportsPage),
};

const pay2000 = {
    "/report-by-platform": () => load(ReportByPlatform),
}

const routes = {
    ...nonProtectedRoutes,
    ...protectedRoutes,
    ...pay2000,
    "/*": () => load(E404Page),
};

export const secureRoutes = {
    ...protectedRoutes,
};

export const inSecureRoutes = {
    ...nonProtectedRoutes,
};

export const routesPay2000 = {
    ...pay2000,
};

export function isPathProtected(path) {
    //return Object.keys(protectedRoutes).includes(path);
    const keys = Object.keys(protectedRoutes);

    return keys.some(key => {
        if (key === path) return true;

        if (key.endsWith("*")) {
            const base = key.replace("*", "");
            return path.startsWith(base);
        }

        if (path.startsWith(key + "/")) return true;

        return false;
    });
}

export function isPathProtectedPay2000(path) {
    return Object.keys(pay2000).includes(path);
}

export function isPreAuthenticationPath(path) {
    return Object.keys(nonProtectedRoutes).includes(path);
}

export function setReturnUrl(path) {
    if (isPathProtected(path)) {
        localStorage.setItem(RETURN_PATH_LOCAL_STORAGE_KEY, path);
    }
}

export function trySetReturnUrl(path) {
    if (isPathProtected(path)) {
        if (localStorage.getItem(RETURN_PATH_LOCAL_STORAGE_KEY) == null) {
            localStorage.setItem(RETURN_PATH_LOCAL_STORAGE_KEY, path);
        }
    }
}

export function navigateReturnUrl(fallback) {
    let returnUrl = localStorage.getItem(RETURN_PATH_LOCAL_STORAGE_KEY) || fallback;
    localStorage.removeItem(RETURN_PATH_LOCAL_STORAGE_KEY);
    navigate(returnUrl || "/home");
}

export default routes;